import BellAlertIcon from "./icons/BellAlertIcon";
import CreditCardIcon from "./icons/CreditCardIcon";
import ShareIcon from "./icons/ShareIcon";
import SwatchIcon from "./icons/SwatchIcon";
import UsersIcon from "./icons/UsersIcon";
import OnboardingSteps from "./OnboardingSteps";
import Link from "next/link";
import { useAppRouter } from "@/src/useAppRouter";
import SimpleDate from "@/lib/SimpleDate";
import { pluralCount } from "@/lib/string";
import classNames from "classnames";
import QuestionMarkCircleIcon from "./icons/QuestionMarkCircleIcon";
import DocumentTextIcon from "./icons/DocumentTextIcon";
import useCurrentOrg from "@/src/useCurrentOrg";
import WarningIcon from "./icons/WarningIcon";
import useRpc from "@/src/useRpc";
import CalendarIcon from "./icons/CalendarIcon";
import PhotoIcon from "./icons/PhotoIcon";
import AssistantChat from "./AssistantChat";
import CogSixToothIcon from "./icons/CogSixToothIcon";
import SparklesIcon from "./icons/SparklesIcon";
import { useCallback, useState } from "react";
import { useMessage } from "@/src/useMessage";


export default function LeftNav ({date}) {

  const router = useAppRouter()
  const activeMenu = router.query.menu

  const org  = useCurrentOrg()
  const [user] = useRpc('users.me')

  const [billing] = useRpc('billing.info')
  const [status] = useRpc('social.status')
  const [contentPlans, , reloadContentPlans] = useRpc('contentPlans.all')

  const chatEnabled = org?.config?.chatbot
  const [activeTab, setActiveTab] = useState('settings')

  const today = new SimpleDate()
  const trialEndDate = billing && new SimpleDate(billing.trialEndDate)

  const daysLeftInTrial = trialEndDate ? today.daysBetween(trialEndDate) : 0

  // Don't show the trial days remaining if the org has added a credit card
  // (they will convert to paid when the trial ends)
  const showTrialDaysRemaining = org?.status === 'trialing' && !billing?.creditCard

  const contentPlanBuilding = contentPlans?.some(p => p.status === 'building')

  const handleRefreshPlans = useCallback(() => reloadContentPlans(), [reloadContentPlans])
  useMessage('refreshContentPlans', handleRefreshPlans)

  const hasGallery = org?.config?.gallery
  const hasTemplates = ['org', 'both'].includes(org?.templateSource)
  const showImageTemplates = hasTemplates || hasGallery

  // Check to see if there are any social accounts that need to be reconnected
  let socialReconnectNeeded = false
  if (status) {
    const expiringSoon = status.destinations.find(dest => {
      return dest.daysUntilDisconnect < 14
    })
    if (expiringSoon) socialReconnectNeeded = true

    const needsReconnectForMetrics = status.destinations.find(dest => {
      return dest.needsReconnectForMetrics
    })
    if (needsReconnectForMetrics && org?.metricsEnabled) {
      socialReconnectNeeded = true
    }
  }

  return <>
    <div className="flex-col bg-white full-height overflow-hidden" style={{borderRight: '1px solid #ddd', width: 300, minWidth: 300}}>

      { chatEnabled &&
        <div className="tab-bar flex-row">
          <button
            className={classNames("tab-button flex-grow flex-row flex-centered gap-1", {active: activeTab === 'settings'})}
            onClick={() => setActiveTab('settings')}
          >
            <span className="tab-icon"><CogSixToothIcon/></span>
            Settings
          </button>
          <button
            className={classNames("tab-button flex-grow flex-row flex-centered gap-1", {active: activeTab === 'chat'})}
            onClick={() => setActiveTab('chat')}
          >
            <span className="tab-icon"><SparklesIcon/></span>
            AI Assistant
          </button>
        </div>
      }

      { activeTab === 'chat' && chatEnabled ?
        <div className="flex-grow overflow-hidden">
          <AssistantChat visibleDate={date}/>
        </div>
      :
        <div className="flex-col flex-grow overflow-auto-y">
          { !!org &&
            <>
              <div className="setup-progress">
                <div className="relative">
                  <OnboardingSteps org={org}/>
                </div>
              </div>
            </>
          }

          { org?.showBilling &&
            <Link href={router.path('subscription')}>
              <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'subscription'})}>
                <div className="flex-row spread">
                  <div className="flex-row gap-3">
                    <span className="menu-icon flex"><CreditCardIcon/></span>
                    <span>
                      Billing
                    </span>
                  </div>
                  <div>
                    { billing?.lastInvoice?.status === 'failed' ?
                      <span className="tiny pt-1 red">
                        Payment failed!
                      </span>
                    : showTrialDaysRemaining &&
                      <span className={classNames("tiny pt-1 dark-gray", {red: daysLeftInTrial <= 3})}>
                        { daysLeftInTrial <= 0 ?
                          <>Last day of</>
                        :
                          <>{ pluralCount(daysLeftInTrial, 'day')} left in</>
                        } free trial
                      </span>
                    }
                  </div>
                </div>
                <div className="menu-subtitle">
                  Subscription and Payment Info
                </div>
              </a>
            </Link>
          }

          <Link href={router.path('brand')}>
            <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'brand'})}>
              <div className="flex-row gap-3">
                <span className="menu-icon flex"><SwatchIcon/></span>
                Branding
              </div>
              <div className="menu-subtitle">
                Your Logos and Colors
              </div>
            </a>
          </Link>

          <Link href={router.path('captions')}>
            <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'captions'})}>
              <div className="flex-row gap-3">
                <span className="menu-icon flex"><DocumentTextIcon/></span>
                <span className="flex-row gap-2">
                  Captions
                </span>
                </div>
              <div className="menu-subtitle">
                Hashtags and Contact Info
              </div>
            </a>
          </Link>

          <Link href={router.path('content')}>
            <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'content'})}>
              <div className="flex-row gap-3 spread">
                <div className="flex-row gap-3">
                  <span className="menu-icon flex"><CalendarIcon/></span>
                  <span className="flex-row gap-2">
                    Content Plan
                  </span>
                </div>
                { contentPlanBuilding &&
                  <span className="nav-spinner" />
                }
              </div>
              <div className="menu-subtitle">
                Topics and Frequency
              </div>
            </a>
          </Link>

          { showImageTemplates &&
            <Link href={router.path('templates')}>
              <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'templates'})}>
                <div className="flex-row gap-3">
                  <span className="menu-icon flex"><PhotoIcon/></span>
                  { hasGallery ? 'Images & Templates' : 'Image Templates' }
                </div>
                <div className="menu-subtitle">
                  { hasGallery ? 'Gallery, Backgrounds, and Layouts' : 'Backgrounds and Layouts' }
                </div>
              </a>
            </Link>
        }

          <Link href={router.path('help')}>
            <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'help'})}>
              <div className="flex-row gap-3">
                <span className="menu-icon flex"><QuestionMarkCircleIcon/></span>
                Help
              </div>
              <div className="menu-subtitle">
                Ask Questions or Report Problems
              </div>
            </a>
          </Link>

          <Link href={router.path('notifications')}>
            <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'notifications'})}>
              <div className="flex-row gap-2 spread">
                <div className="flex-row gap-3">
                  <span className="menu-icon flex"><BellAlertIcon/></span>
                  Notifications
                </div>
                { user?.emailBlocked &&
                  <div className="text-xs">
                    <div className="red flex-row gap-1 flex-align-center">
                      <WarningIcon/>
                      Email Issue
                    </div>
                  </div>
                }
              </div>
              <div className="menu-subtitle">
                Email Preferences
              </div>
            </a>
          </Link>

          <Link href={router.path('social')}>
            <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'social'})}>
              <div className="flex-row gap-2 spread">
                <div className="flex-row gap-3">
                  <span className="menu-icon flex"><ShareIcon/></span>
                  <div className="flex-row gap-3">
                    Publishing
                  </div>
                </div>
                { (socialReconnectNeeded || (org && !org?.autoPublish)) &&
                  <div className="text-xs">
                    { socialReconnectNeeded ?
                      <div className="red flex-row gap-1 flex-align-center">
                        <WarningIcon/>
                        <span> Reconnect Needed </span>
                      </div>
                    :
                      <span className="red">(Auto-Publish is Off)</span>
                    }
                  </div>
                }
              </div>
              <div className="menu-subtitle">
                Social Accounts and Scheduling
              </div>
            </a>
          </Link>

          <Link href={router.path('users')}>
            <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'users'})}>
              <div className="flex-row gap-3">
                <span className="menu-icon flex"><UsersIcon/></span>
                Team
                </div>
              <div className="menu-subtitle">
                Co-Workers and Collaborators
              </div>
            </a>
          </Link>

          { org?.config?.apiAccess &&
            <Link href={router.path('api')}>
              <a className={classNames("flex-col menu-item relative", {active: activeMenu === 'api'})}>
                <div className="flex-row gap-3">
                  <span className="menu-icon flex"><CogSixToothIcon/></span>
                  API Access
                </div>
                <div className="menu-subtitle">
                  Manage Connected Apps
                </div>
              </a>
            </Link>
          }
        </div>
      }

    </div>


    <style jsx>{`
      hr {
        margin: 0.5rem 0;
      }
      .tab-bar {
        border-bottom: 2px solid #eee;
        flex-shrink: 0;
      }
      .tab-button {
        padding: 0.6rem 1rem;
        font-size: 0.85rem;
        font-weight: 600;
        color: #999;
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        margin-bottom: -2px;
        cursor: pointer;
        transition: all 200ms;
      }
      .tab-icon :global(svg) {
        width: 1rem;
        height: 1rem;
      }
      .tab-button:hover {
        color: #666;
      }
      .tab-button.active {
        color: #333;
        border-bottom-color: #485fc7;
      }
      .setup-progress {
        background-color: #dcdbff;
      }
      @media (min-width: 885px) {
        .setup-progress {
          margin-right: -1px;
          border-right: 1px solid #66669911;
        }
      }
      .menu-item, .menu-icon, .menu-subtitle {
        transition: all 250ms;
      }
      .menu-item {
        display: block;
        border-bottom: 1px solid #eee;
        color: #666;
        position: relative;
        font-weight: 500;
        padding: 0.5rem 1rem;
      }
      .menu-item.active,
      .menu-item:hover {
        color: #333;
        background-color: #e1e1ff88;
        background-color: #c7ecff77;
        border-color: #c7ecff33;
      }
      .menu-item.active {
        z-index: 1;
        background-color: #e1e1ff;
        background-color: #c7ecff;
        cursor: default !important;
      }
      .menu-icon {
        width: 24px;
        padding-top: 2px;
        color: #aaa;
      }
      .menu-item.active .menu-icon,
      .menu-item:hover .menu-icon {
        color: #666;
      }
      .menu-subtitle {
        font-size: 0.8rem;
        font-weight: normal;
        color: #999;
        margin-left: 2.25rem;
      }
      .menu-item.active .menu-subtitle,
      .menu-item:hover .menu-subtitle {
        color: #666;
      }
      .nav-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid #ddd;
        border-top-color: #2f8ab8;
        border-left-color: #2f8ab8;
        border-radius: 50%;
        animation: nav-spin 0.6s linear infinite;
        flex-shrink: 0;
      }
      @keyframes nav-spin {
        to { transform: rotate(360deg); }
      }

    `}</style>
  </>
}
